'use client';

import React, { useState, useEffect } from 'react';
import { AlertTriangle, Package, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

interface LowStockItem {
  id: string;
  partNumber: string;
  name: string;
  stock: number;
  min: number;
}

export default function LowStockAlertsPanel() {
  const [items, setItems] = useState<LowStockItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = async () => {
    const supabase = createClient();

    const { data: allParts, error: err2 } = await supabase
      .from('parts')
      .select('id, part_number, name, stock, min_stock')
      .order('stock', { ascending: true });

    if (err2) {
      console.error('LowStockAlertsPanel error:', err2.message);
      setLoading(false);
      return;
    }

    const lowStock = (allParts || [])
      .filter((p) => (p.stock as number) < (p.min_stock as number))
      .slice(0, 6)
      .map((p) => ({
        id: p.id as string,
        partNumber: p.part_number as string,
        name: p.name as string,
        stock: p.stock as number,
        min: p.min_stock as number,
      }));

    setItems(lowStock);
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();

    const supabase = createClient();
    const channel = supabase
      .channel('low_stock_panel')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'parts' }, () => {
        fetchItems();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <div className="card-surface p-5 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center">
            <AlertTriangle size={16} className="text-warning" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">Low Stock Alerts</h2>
            <p className="text-xs text-muted-foreground">
              {loading ? 'Loading…' : `${items.length} parts need restocking`}
            </p>
          </div>
        </div>
        <Link href="/inventory-management" className="text-xs text-primary hover:underline flex items-center gap-1">
          View all <ArrowRight size={12} />
        </Link>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto scrollbar-thin">
        {loading ? (
          <div className="text-xs text-muted-foreground py-4 text-center">Loading inventory…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-muted-foreground py-4 text-center">All parts are adequately stocked.</div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors duration-150 group"
              style={{ backgroundColor: item.stock === 0 ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.05)' }}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${item.stock === 0 ? 'bg-danger/10' : 'bg-warning/10'}`}>
                <Package size={14} className={item.stock === 0 ? 'text-danger' : 'text-warning'} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground truncate">{item.name}</p>
                <p className="text-xs font-mono-data text-muted-foreground">{item.partNumber}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`text-sm font-bold font-mono-data ${item.stock === 0 ? 'text-danger' : 'text-warning'}`}>
                  {item.stock}
                </p>
                <p className="text-xs text-muted-foreground">of {item.min} min</p>
              </div>
            </div>
          ))
        )}
      </div>
      <Link href="/inventory-management" className="btn-secondary w-full justify-center mt-4 text-xs">
        <Package size={14} />
        Restock Parts
      </Link>
    </div>
  );
}